import { API_BASE_URL, FILES_BASE_URL } from "../../config";
import React from 'react';
import { Modal, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { useOrdersByIds } from './hooks/use-orders-by-ids';
import {  formatDateDayMonthYear } from '../../utils';

interface OrdersModalProps {
  show: boolean;
  onHide: () => void;
  orderIds: string[];
  currency: string;
  period?: {
    start: string;
    end: string;
  };
  settlementType?: 'INFLUENCER_BONUS' | 'LOYALTY_ROYALTY';
  title?: string;
}

const OrdersModal: React.FC<OrdersModalProps> = ({
  show,
  onHide,
  orderIds,
  currency,
  period,
  settlementType,
  title = "Órdenes"
}) => {
  const { userTotals, isLoading, error } = useOrdersByIds(orderIds, show);

  return (
    <Modal show={show} onHide={onHide} size="xl" className="orders-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <h4 className="mb-1">{title}</h4>
            {period && (
              <small className="text-muted">
                Período: {formatDateDayMonthYear(period.start)} - {formatDateDayMonthYear(period.end)} | Moneda: {currency}
              </small>
            )}
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Cargando órdenes...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">
            Error al cargar las órdenes. Por favor, intenta nuevamente.
          </Alert>
        ) : (
          <>
            {/* Tabla de órdenes agrupadas por usuario */}
            <div className='default-table-area'>
              <div className="table-responsive">
                <Table striped hover>
                  <thead className="table-dark">
                    <tr>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Productos</th>
                      <th>Total Puntos</th>
                      <th>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userTotals.map((userTotal, userIndex) => (
                      userTotal.orders.map((order, orderIndex) => (
                        <React.Fragment key={`${userIndex}-${orderIndex}`}>
                          {order.items.map((item, itemIndex) => (
                            <tr key={`${userIndex}-${orderIndex}-${itemIndex}`}>
                              {/* Usuario (solo en la primera fila del usuario) */}
                              {orderIndex === 0 && itemIndex === 0 && (
                                <td rowSpan={userTotal.orders.reduce((sum, o) => sum + o.items.length, 0)}>
                                  <div className="d-flex align-items-center">
                                    {userTotal.user.photo && (
                                      <img
                                        src={`${API_BASE_URL}/users/show/photo/${userTotal.user.photo}`}
                                        alt={userTotal.user.name}
                                        className="rounded-circle me-2"
                                        width="32"
                                        height="32"
                                      />
                                    )}
                                    <strong>{userTotal.user.name}</strong>
                                  </div>
                                </td>
                              )}

                              {/* Email (solo en la primera fila del usuario) */}
                              {orderIndex === 0 && itemIndex === 0 && (
                                <td rowSpan={userTotal.orders.reduce((sum, o) => sum + o.items.length, 0)}>
                                  <small className="text-muted">{userTotal.user.email}</small>
                                </td>
                              )}

                              {/* Producto */}
                              <td>
                                <div className="d-flex align-items-center">
                                  {item.product_id.images?.[item.product_id.mainImage || 0] && (
                                    <img
                                      src={`${FILES_BASE_URL}/${item.product_id.images[item.product_id.mainImage || 0]}`}
                                      alt={item.product_id.title}
                                      className="me-2"
                                      width="40"
                                      height="40"
                                      style={{ objectFit: 'cover' }}
                                    />
                                  )}
                                  <div>
                                    <div className="fw-medium">{item.product_id.title}</div>
                                    <small className="text-muted">Cantidad: {item.quantity}</small>
                                  </div>
                                </div>
                              </td>

                              {/* Puntos del item */}
                              <td>
                                <Badge color="success" pill>
                                  {settlementType === 'INFLUENCER_BONUS'
                                    ? (item.influencer_bonus * item.quantity).toLocaleString()
                                    : (item.loyalty_points * item.quantity).toLocaleString()
                                  }
                                </Badge>
                              </td>

                              {/* Fecha (solo en la primera fila de cada orden) */}
                              {itemIndex === 0 && (
                                <td rowSpan={order.items.length}>
                                  <small>{formatDateDayMonthYear(order.created_at)}</small>
                                </td>
                              )}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    ))}

                    {userTotals.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-4">
                          <div className="text-muted">
                            <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                            No se encontraron órdenes
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrdersModal;
